<template>
  <div>
    <div class="main-content fe-account-page">
      <div class="container py-4 py-lg-5">
        <header class="fe-account-page__header">
          <h1 class="fe-account-page__title">My orders</h1>
          <p class="fe-account-page__subtitle">
            Track and review everything you’ve purchased.
          </p>
        </header>

        <div class="row g-4 align-items-start">
          <div class="col-lg-3 col-md-4">
            <account-sidebar />
          </div>
          <div class="col-lg-9 col-md-8">
            <section class="fe-account-card fe-orders-card">
              <div class="fe-account-card__head">
                <h2 class="fe-account-card__title">Order history</h2>
                <span class="fe-account-card__tag">{{ orders.length }} total</span>
              </div>

              <p v-if="orders.length === 0" class="fe-orders-empty">
                <router-link to="/shop" class="fe-orders-empty__link">
                  <i class="fas fa-shopping-bag"></i>
                  Start shopping
                </router-link>
                You haven’t placed any orders yet.
              </p>

              <div v-else class="table-responsive fe-orders-table-wrap">
                <table class="table fe-orders-table mb-0">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Placed on</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="order in orders" :key="order.id">
                      <td data-label="Order #">{{ order.reference }}</td>
                      <td data-label="Placed on">{{ formatDate(order.system_date) }}</td>
                      <td data-label="Total">{{ order.total_order_value }}</td>
                      <td data-label="Status">
                        <span class="fe-orders-status">{{ order.order_approval_status }}</span>
                      </td>
                      <td class="text-end">
                        <router-link
                          :to="'/order/' + order.id"
                          class="btn btn-sm fe-orders-view-btn"
                        >
                          <i class="fas fa-eye"></i> View
                        </router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <cta-area></cta-area>
  </div>
</template>

<script>
import CtaArea from '@/components/cta/cta-area.vue'
import AccountSidebar from '@/components/account/AccountSidebar.vue'

export default {
  name: 'MyOrdersPage',
  components: {
    CtaArea,
    AccountSidebar,
  },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user')) || { name: 'Guest', email: '' },
      orders: [],
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.user
    },
  },
  methods: {
    async fetchOrdersByUserId(userId) {
      try {
        const response = await this.$axios.get(`/orders/user/${userId}`)
        this.orders = response.data
      } catch (error) {
        console.error('Error fetching orders:', error.response?.data || error)
      }
    },
    formatDate(dateString) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return new Date(dateString).toLocaleDateString(undefined, options)
    },
  },
  mounted() {
    if (this.isLoggedIn) {
      this.fetchOrdersByUserId(this.user.id)
    }
  },
}
</script>

<style scoped>
.fe-account-page {
  background: linear-gradient(180deg, #f8f8f8 0%, #fff 120px);
}

.fe-account-page__header {
  text-align: center;
  margin-bottom: 2rem;
}

.fe-account-page__title {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #141414;
  margin: 0 0 0.5rem;
}

.fe-account-page__subtitle {
  margin: 0 auto;
  max-width: 520px;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
}

.fe-account-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ececec;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
  padding: 1.35rem 1.5rem 1.5rem;
}

.fe-account-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #eee;
}

.fe-account-card__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
  color: #141414;
}

.fe-account-card__tag {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 16%, #fff);
  color: #5c4a00;
}

.fe-orders-empty {
  margin: 0;
  padding: 1.25rem 0;
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
}

.fe-orders-empty__link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 0.5rem;
  font-weight: 700;
  color: var(--fe-accent, #d4a000);
  text-decoration: none;
}

.fe-orders-empty__link:hover {
  text-decoration: underline;
}

.fe-orders-table-wrap {
  border-radius: 12px;
  border: 1px solid #eee;
  overflow: hidden;
}

.fe-orders-table {
  margin: 0;
  font-size: 0.9rem;
}

.fe-orders-table thead th {
  background: #fafafa;
  border-bottom: 1px solid #eee;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666;
  white-space: nowrap;
  padding: 0.75rem 1rem;
}

.fe-orders-table tbody td {
  padding: 0.85rem 1rem;
  vertical-align: middle;
  border-color: #f0f0f0;
  color: #333;
}

.fe-orders-status {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  background: #f5f5f5;
  color: #444;
}

.fe-orders-view-btn {
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.35rem 0.85rem;
  border: 1px solid color-mix(in srgb, var(--fe-accent, #fcbe00) 50%, #ccc);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 12%, #fff) 0%,
    #fff 100%
  );
  color: #111;
}

.fe-orders-view-btn:hover {
  border-color: var(--fe-accent, #fcbe00);
  color: #111;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 14%, #fff);
}

@media (max-width: 767.98px) {
  .fe-orders-table thead {
    display: none;
  }

  .fe-orders-table tbody tr {
    display: block;
    border-bottom: 1px solid #eee;
    padding: 0.75rem 0;
  }

  .fe-orders-table tbody td {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    border: none;
    padding: 0.35rem 0.75rem;
  }

  .fe-orders-table tbody td::before {
    content: attr(data-label);
    font-weight: 700;
    color: #888;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .fe-orders-table tbody td.text-end {
    justify-content: flex-end;
  }

  .fe-orders-table tbody td.text-end::before {
    display: none;
  }
}
</style>